import { istTeam, pinzielSchluessel, tagKompakt, type Pinziel } from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { PinOff } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useAbpinnen, usePinnen } from '../lib/mutationen'
import { schirmQuery, sitzungQuery } from '../lib/queries'
import { Merkfeld } from './Merkfeld'

/**
 * **📌 – der Hauptweg auf den Schirm** ([Z-03](../../../../docs/phasen/zusatz-03-auf-dem-schirm.md)).
 *
 * ## Ein Klick pinnt. Das Datum kommt gleich danach.
 *
 * Der erste Entwurf ließ das Anpinnen dialogfrei und legte Notiz und Wiedervorlage
 * **ausschließlich** an die gepinnte Zeile in „Mein Tag". Die Regel dahinter war richtig – ein
 * Formular vor einer Ein-Klick-Geste, und niemand pinnt mehr. Der Ort war es nicht.
 *
 * Nils, 24.08.2026, beim ersten Ausprobieren: *„sehe wenn ich die PIN drücke kein Datumsfeld
 * oder so."* Genau der Fehler, den das Tagesziel schon einmal vermieden hat
 * ([Z-02.11](../../../../docs/phasen/zusatz-02-mein-tag.md)): **ein Feld, das man an der Stelle
 * der Absicht nicht findet, gibt es nicht.** Wer eine Wiedervorlage setzen will, denkt in dem
 * Moment daran, in dem er anpinnt.
 *
 * Jetzt gilt beides: der Klick **pinnt sofort** – bestätigt, ohne Rückfrage –, und *danach*
 * klappt das {@link Merkfeld} auf. Wer nur pinnen wollte, hat schon gepinnt und klickt es weg.
 * Ein Dialog **vor** dem Pinnen wäre eine Hürde, einer danach ist ein Angebot.
 *
 * Bei einem **bereits gepinnten** Ziel öffnet der Knopf nur das Feld statt abzupinnen –
 * abgepinnt wird darin oder in der Zone. Sonst wäre der zweite Klick auf denselben Knopf
 * zerstörerisch, und man müsste sich merken, welcher Klick was tut.
 *
 * ## Und mit der Taste `p`
 *
 * Leitplanke 3 – alles per Tastatur erreichbar. Sie wirkt nur, solange dieses Bauteil im Bild
 * ist, und **nicht**, während jemand in einem Feld tippt: sonst wäre jedes „p" in einem
 * Kommentar ein Pin.
 *
 * ## Wer ihn nicht sieht
 *
 * Mit-Owner und Gäste. Sie haben kein „Mein Tag" ([E-109](../../../../docs/ENTSCHEIDUNGEN.md)),
 * also auch keinen Ort, an dem ein Pin erschiene – ein Knopf, der in einen Raum ohne Boden
 * führt, ist schlechter als keiner. Dieselbe Grenze wie am Endpunkt (`teamProcedure`), nur
 * sichtbar statt als Fehlermeldung.
 */
export function PinKnopf({ taskId, vorhabenId }: { taskId?: string; vorhabenId?: string }) {
  const { data: sitzung } = useQuery(sitzungQuery)
  const imTeam = sitzung ? istTeam(sitzung.role) : false

  const { data: schirm } = useQuery({ ...schirmQuery, enabled: imTeam })
  const pinnen = usePinnen()
  const abpinnen = useAbpinnen()
  const [feldOffen, setFeldOffen] = useState(false)

  const ziel: Pinziel | null = taskId
    ? { art: 'aufgabe', taskId }
    : vorhabenId
      ? { art: 'vorhaben', vorhabenId }
      : null

  const schluessel = ziel ? pinzielSchluessel(ziel) : null
  const pin = schluessel
    ? schirm?.pins.find((p) => p.art === 'gepinnt' && pinzielSchluessel(p.ziel) === schluessel)
    : undefined
  const gepinnt = Boolean(pin)

  const anstossen = () => {
    if (!ziel) return
    /** Noch nicht gepinnt: erst pinnen (sofort, optimistisch), dann das Feld anbieten. */
    if (!gepinnt) pinnen.mutate(ziel)
    setFeldOffen(true)
  }

  /** Das Feld schließt sich, sobald das Fenster ein anderes Ziel zeigt. */
  useEffect(() => setFeldOffen(false), [schluessel])

  useEffect(() => {
    if (!imTeam || !ziel) return
    const beiTaste = (e: KeyboardEvent) => {
      if (e.key !== 'p' && e.key !== 'P') return
      if (e.metaKey || e.ctrlKey || e.altKey) return
      const wo = e.target as HTMLElement | null
      if (
        wo &&
        (wo.tagName === 'INPUT' ||
          wo.tagName === 'TEXTAREA' ||
          wo.tagName === 'SELECT' ||
          wo.isContentEditable)
      ) {
        return
      }
      e.preventDefault()
      anstossen()
    }
    window.addEventListener('keydown', beiTaste)
    return () => window.removeEventListener('keydown', beiTaste)
  })

  if (!imTeam || !ziel) return null

  return (
    <div className="relative">
      <button
        type="button"
        onClick={anstossen}
        aria-pressed={gepinnt}
        aria-expanded={feldOffen}
        className={`cursor-pointer rounded-lg px-2 py-1 text-[13px] hover:bg-[#f3f2ef] ${
          gepinnt ? 'opacity-100' : 'opacity-45'
        }`}
        title={
          gepinnt
            ? `Angepinnt${pin?.wiedervorlage ? ` · liegt wieder vor am ${tagKompakt(pin.wiedervorlage)}` : ''} – Notiz und Wiedervorlage bearbeiten (Taste p)`
            : 'Anpinnen – legt sie in „Mein Tag" unter „Auf dem Schirm" (Taste p)'
        }
        aria-label={gepinnt ? 'Notiz und Wiedervorlage bearbeiten' : 'Anpinnen'}
      >
        📌
        {/**
         * **Das gesetzte Datum steht am Knopf.** Ohne diese Marke sieht ein Pin mit
         * Wiedervorlage genauso aus wie einer ohne – und man setzt sie ein zweites Mal.
         */}
        {pin?.wiedervorlage && (
          <span className="ml-1 text-[10.5px] font-semibold text-accent-deep">
            {tagKompakt(pin.wiedervorlage)}
          </span>
        )}
      </button>

      {feldOffen && (
        <div className="absolute top-full right-0 z-10 mt-1 w-[min(380px,calc(100vw-32px))] rounded-lg bg-surface shadow-card">
          <Merkfeld
            ziel={ziel}
            notiz={pin?.notiz ?? null}
            wiedervorlage={pin?.wiedervorlage ?? null}
            schliessen={() => setFeldOffen(false)}
          />
          {/**
           * **Das Abpinnen war da, nur nicht zu sehen** (21.09.2026, aus der Review-Aufgabe:
           * *„Einmal angepinnte Aufgabe kann man nicht mehr unpinnen?"*).
           *
           * Es stand als graue, unterstrichene Kleinschrift am Fuß des Merkfeldes – die Form,
           * die man für „Impressum" benutzt, nicht für die einzige Gegenhandlung zum Anpinnen.
           * Jetzt ein sichtbarer Knopf mit dem durchgestrichenen Zeichen: Wer das Feld öffnet,
           * findet ihn, ohne ihn zu suchen.
           */}
          <div className="border-t border-line px-2.5 py-2">
            <button
              type="button"
              onClick={() => {
                abpinnen.mutate(ziel)
                setFeldOffen(false)
              }}
              className="flex w-full cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1.5 text-[12px] font-semibold text-ink2 hover:bg-[#f3f2ef]"
            >
              <PinOff aria-hidden="true" className="size-3.5" />
              Wieder abpinnen
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
